import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NgApexchartsModule } from 'ng-apexcharts';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { DashboardService } from '../../../core/services/dashboard.service';
import { ProcessTimelineData, ProcessesTimelineResponse } from '../../../core/models/dashboard.model';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-ui-processes-timeline',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule, LoadingSpinnerComponent],
  templateUrl: './ui-processes-timeline.component.html'
})
export class UiProcessesTimelineComponent implements OnInit, OnDestroy {
  public timelineData: ProcessesTimelineResponse['data'] | null = null;
  public loading = false;
  public error = false;
  public chartOptions: any;
  public hasData = false;

  private destroy$ = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    private authService: AuthService
  ) {
    // Não inicializa o gráfico aqui - será inicializado apenas quando houver dados
  }

  ngOnInit(): void {
    this.loadProcessesTimeline();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadProcessesTimeline(): void {
    // Verificar se o usuário está autenticado
    if (!this.authService.isAuthenticated()) {
      console.warn('Usuário não autenticado. Não é possível carregar timeline dos processos.');
      this.error = true;
      return;
    }

    this.loading = true;
    this.error = false;

    this.dashboardService.getProcessesTimeline()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.timelineData = response.data;
            this.hasData = false;
            
            // Verifica se há dados relevantes na timeline
            const hasCreatedData = response.data.created && response.data.created.length > 0;
            const hasCompletedData = response.data.completed && response.data.completed.length > 0;
            
            if (hasCreatedData || hasCompletedData) {
              this.hasData = true;
              this.updateChartData(response.data);
            }
            
            this.loading = false;
          } else {
            console.warn('Resposta da API sem dados válidos:', response);
            this.hasData = false;
            this.error = true;
            this.loading = false;
          }
        },
        error: (error) => {
          console.error('Erro ao carregar timeline dos processos:', error);
          this.hasData = false;
          this.handleApiError(error);
          this.loading = false;
        }
      });
  }

  /**
   * Atualiza os dados do gráfico com base nos dados de timeline recebidos
   * @param timelineData Dados de timeline da API
   */
  private updateChartData(timelineData: ProcessesTimelineResponse['data']): void {
    const createdData = timelineData.created.map(item => item.count);
    const completedData = timelineData.completed.map(item => item.count);
    const categories = timelineData.labels;

    this.chartOptions = {
      series: [
        {
          name: 'Criados',
          data: createdData,
        },
        {
          name: 'Concluídos',
          data: completedData,
        },
      ],
      colors: ['#845adf', '#23b7e5'],
      chart: {
        height: 335,
        type: 'line',
        dropShadow: {
          enabled: true,
          color: '#000',
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.2,
        },
        toolbar: {
          show: false,
        },
        events: {
          mounted: (chart: any) => {
            chart.windowResizeHandler();
          },
        },
      },
      dataLabels: {
        enabled: true,
      },
      stroke: {
        curve: 'smooth',
      },
      title: {
        text: 'Evolução Temporal dos Processos',
        align: 'left',
        style: {
          fontSize: '13px',
          fontWeight: 'bold',
          color: '#8c9097',
        },
      },
      grid: {
        borderColor: '#e7e7e7',
        row: {
          colors: ['#f3f3f3', 'transparent'],
          opacity: 0.5,
        },
      },
      markers: {
        size: 6,
        hover: {
          size: 10,
        },
      },
      xaxis: {
        categories: categories,
        title: {
          text: 'Data',
          style: {
            color: '#8c9097',
            fontSize: '13px',
            fontWeight: 'bold',
          },
        },
        labels: {
          show: true,
          style: {
            colors: '#8c9097',
            fontSize: '11px',
            fontWeight: 600,
            cssClass: 'apexcharts-xaxis-label',
          },
        },
      },
      yaxis: {
        title: {
          text: 'Quantidade',
          style: {
            color: '#8c9097',
            fontSize: '13px',
            fontWeight: 'bold',
          },
        },
        labels: {
          show: true,
          style: {
            colors: '#8c9097',
            fontSize: '11px',
            fontWeight: 600,
            cssClass: 'apexcharts-yaxis-label',
          },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        floating: true,
        offsetY: -25,
        offsetX: -5,
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val + ' processos';
          },
        },
      },
    };
  }

  /**
   * Trata erros específicos da API
   * @param error Erro recebido da API
   */
  private handleApiError(error: any): void {
    this.error = true;
    
    // Log específico baseado no tipo de erro
    if (error.status === 401) {
      console.warn('Token não fornecido, inválido ou expirado');
    } else if (error.status === 500) {
      console.error('Erro interno do servidor');
    } else {
      console.error('Erro desconhecido ao carregar timeline dos processos:', error);
    }
  }
}
